// shell/components/disposable.ts

import { IDisposable } from './types';

/**
 * Tracks multiple disposables and disposes them together.
 * Useful for components that create multiple subscriptions/observers.
 *
 * Usage:
 * ```typescript
 * class MyComponent implements IDisposable {
 *     private disposables = new DisposableStore();
 *
 *     constructor() {
 *         this.disposables.add(eventEmitter.on('change', this.handleChange));
 *         this.disposables.add({ dispose: () => observer.disconnect() });
 *     }
 *
 *     dispose() {
 *         this.disposables.dispose();
 *     }
 * }
 * ```
 */
export class DisposableStore implements IDisposable {
    private disposables: IDisposable[] = [];
    private disposed = false;

    /**
     * Add a disposable or cleanup function to the store.
     */
    add(disposable: IDisposable | (() => void)): void {
        if (this.disposed) {
            // Already disposed - dispose immediately
            if (typeof disposable === 'function') {
                disposable();
            } else {
                disposable.dispose();
            }
            return;
        }

        if (typeof disposable === 'function') {
            this.disposables.push({ dispose: disposable });
        } else {
            this.disposables.push(disposable);
        }
    }

    /**
     * Dispose all tracked disposables.
     * Safe to call multiple times.
     */
    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;

        for (const disposable of this.disposables) {
            try {
                disposable.dispose();
            } catch (e) {
                console.error('Error during dispose:', e);
            }
        }
        this.disposables = [];
    }

    /**
     * Check if already disposed.
     */
    isDisposed(): boolean {
        return this.disposed;
    }
}

/**
 * Creates a disposable from a cleanup function.
 */
export function toDisposable(fn: () => void): IDisposable {
    return { dispose: fn };
}

/**
 * Combines multiple disposables into one.
 */
export function combineDisposables(...disposables: IDisposable[]): IDisposable {
    return {
        dispose: () => {
            for (const d of disposables) {
                try {
                    d.dispose();
                } catch (e) {
                    console.error('Error during dispose:', e);
                }
            }
        }
    };
}
